import { useSidebar } from "components/ui/sidebar";
import { useLocation, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChatStore } from "../../../state/store/chat/use-chat-store";
import { useProjects } from "../hooks/use-projects";
import { ConversationActions } from "./conversation-actions";
import { ProjectActions } from "./project-actions";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import { isNestedSeeMoreDesign } from "config/sidebar-see-more";
import { useQueryClient } from "@tanstack/react-query";
import { fetchProjectConversations } from "../services/projects-services";
import { useConfig } from "hooks/use-config";
import { Droppable } from "features/sidebar/Dropabble";
import { SidebarTreeItem } from "components/blocks/layout/sidebar-items";
import { ProjectFolderIcon } from "components/blocks/layout/project-folder-icon";
// import { IconName } from "components/blocks/menu-icon/menu-icon";

export const ProjectLists = ({ variant = 'section' }: { variant?: 'default' | 'section' }) => {
    const isSidebarLoading = false;
    const { setOpenMobile, isMobile } = useSidebar();
    const { pathname } = useLocation();
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
    const [showAllProjects, setShowAllProjects] = useState(false);
    const [recentlyAccessedProjects, setRecentlyAccessedProjects] = useState<string[]>([]);
    const navigate = useNavigate();
    const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
    const updateProjectItems = useChatStore((state) => state.updateProjectItems);
    const setSidebarProjectsList = useChatStore((state) => state.setSidebarProjectsList);
    const { projectId } = useParams();
    const { data: projects } = useProjects({
        pageSize: 50,
        page: 1,
        order: "desc",
        searchQuery: "",
    });
    const { t } = useTranslation();
    const { theme } = useTheme();
    const queryClient = useQueryClient();
    const { configurationSettings } = useConfig();

    const [loadingProjects, setLoadingProjects] = useState<Record<string, boolean>>({});
    const [fetchedProjectConversations, setFetchedProjectConversations] = useState<Record<string, boolean>>({});
    const previousConversationRef = useRef<string | null>(null);
    const hasHydratedProjectsRef = useRef(false);



    useEffect(() => {
        const isChatRoute = pathname.startsWith('/chat/');
        if (isChatRoute && sidebarProjectsList) {
            const afterChat = pathname.split('/chat/')[1] ?? '';
            const conversationId = afterChat.split('/')[0];
            const isNewConversation = conversationId !== previousConversationRef.current;
            previousConversationRef.current = conversationId;

            const projectWithConversation = sidebarProjectsList.find(project =>
                project.items?.some(item => item.ConversationId === conversationId)
            );

            if (projectWithConversation && isNewConversation) {
                setOpenGroups(prev => ({
                    ...prev,
                    [projectWithConversation.id]: true
                }));
            }
        }
    }, [pathname, sidebarProjectsList]);

    const toggleGroup = (groupId: string) => {
        setOpenGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };

    useEffect(() => {
        if (!projects?.length || hasHydratedProjectsRef.current) return;

        hasHydratedProjectsRef.current = true;
        setSidebarProjectsList(projects);
    }, [projects, setSidebarProjectsList]);

    useEffect(() => {
        const validProjectIds = new Set(sidebarProjectsList.map((project) => project.id));

        setOpenGroups((prev) =>
            Object.fromEntries(Object.entries(prev).filter(([id]) => validProjectIds.has(id)))
        );
        setFetchedProjectConversations((prev) =>
            Object.fromEntries(Object.entries(prev).filter(([id]) => validProjectIds.has(id)))
        );
        setLoadingProjects((prev) =>
            Object.fromEntries(Object.entries(prev).filter(([id]) => validProjectIds.has(id)))
        );
        setRecentlyAccessedProjects((prev) => prev.filter((id) => validProjectIds.has(id)));
    }, [sidebarProjectsList]);

    if (isSidebarLoading) return null;

    if (!sidebarProjectsList?.length) {
        return (
            <div className="py-1 px-2 text-center">
                <p className="text-xs text-muted-foreground italic mb-2">
                    {t('NO_PROJECTS_YET') || 'No projects yet'}
                </p>
            </div>
        );
    }

    const handleProjectClick = (projectId: string) => {
        if (isMobile) {
            setOpenMobile(false);
        }
        navigate(`/project/${projectId}`);
    }

    const handleProjectClickFromDropdown = (clickedProjectId: string) => {
        setRecentlyAccessedProjects((prev) =>
            [clickedProjectId, ...prev.filter((id) => id !== clickedProjectId)].slice(0, 5)
        );
        handleProjectClick(clickedProjectId);
    };

    const recentlyAccessed = recentlyAccessedProjects
        .map((id) => sidebarProjectsList.find((project) => project.id === id))
        .filter((project): project is NonNullable<typeof project> => Boolean(project));
    const otherProjects = sidebarProjectsList.filter(
        (project) => !recentlyAccessedProjects.includes(project.id)
    );
    const orderedProjects = isNestedSeeMoreDesign
        ? sidebarProjectsList
        : [...recentlyAccessed, ...otherProjects];

    const MAX_VISIBLE_PROJECTS = 5;
    const hiddenProjects = orderedProjects.slice(MAX_VISIBLE_PROJECTS);
    const displayedProjects =
        isNestedSeeMoreDesign && showAllProjects
            ? orderedProjects
            : orderedProjects.slice(0, MAX_VISIBLE_PROJECTS);

    const handleFolderClick = async (projectId: string) => {
        if (projectId in loadingProjects || sidebarProjectsList.find(project => project.id === projectId)?.items?.length) return;

        setLoadingProjects((prev) => ({
            ...prev,
            [projectId]: true,
        }));

        try {
            const conversations = await queryClient.fetchQuery({
                queryKey: ["project-conversations", projectId],
                queryFn: () => fetchProjectConversations(configurationSettings, projectId)
            });

            updateProjectItems(projectId, conversations);
        } catch (error) {
            console.error('Error fetching project conversations:', error);
        } finally {
            setLoadingProjects((prev) => ({
                ...prev,
                [projectId]: false,
            }));
            setFetchedProjectConversations((prev) => ({
                ...prev,
                [projectId]: true,
            }));
        }
    }

    const renderProject = (group: any) => (
        <SidebarTreeItem
            key={group.id}
            label={group.name}
            icon="Folder"
            variant={variant}
            isEntityName
            isActive={projectId === group.id}
            folderColor={group.color}
            defaultOpen={openGroups[group.id] || false}
            onClick={() => {
                toggleGroup(group.id);
                handleFolderClick(group.id);
                handleProjectClick(group.id);
            }}
            rightAction={<ProjectActions projectId={group.id} projectName={group.name} projectColor={group.color} />}
        >
            {loadingProjects[group.id] && (
                <div className="flex items-center gap-2 px-4 py-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>{t('LOADING')}...</span>
                </div>
            )}
            {!loadingProjects[group.id] &&
                fetchedProjectConversations[group.id] &&
                (!group.items || group.items.length === 0) && (
                    <div className="py-1 px-2 text-center">
                        <p className="text-xs text-muted-foreground italic mb-2">
                            {t('NO_CONVERSATIONS_YET') || 'No conversations yet'}
                        </p>
                    </div>
                )}
            {group.items && group.items.length > 0 && (
                <div className="flex flex-col mt-1 gap-1">
                    {group.items.slice(0, 5).map((item: any) => (
                        <SidebarTreeItem
                            key={item.ConversationId}
                            label={item.ConversationTitle || t('NEW_CHAT')}
                            isActive={pathname.includes(item.ConversationId)}
                            depth={1}
                            variant={variant}
                            className={variant === 'section' ? 'py-3.5 pl-9 ' : undefined}
                            onClick={() => {
                                if (isMobile) setOpenMobile(false);
                                navigate(`/chat/${item.ConversationId}/${group.id}`);
                            }}
                            rightAction={<ConversationActions conversationId={item.ConversationId} conversationTitle={item.ConversationTitle} projectId={group.id} projectName={group.name} />}
                        />
                    ))}
                </div>
            )}
            {group.items && group.items.length > 5 && (
                <SidebarTreeItem
                    label={`${t('SEE_ALL')} (${group.items.length})`}
                    depth={1}
                    variant={variant}
                    className={variant === 'section' ? 'py-3.5 pl-9 ' : undefined}
                    onClick={() => handleProjectClick(group.id)}
                />
            )}
        </SidebarTreeItem>
    );

    return (
        <div className="flex flex-col gap-1">
            {displayedProjects.map((group) => (
                <Droppable key={group.id} id={group.id}>
                    {renderProject(group)}
                </Droppable>
            ))}

            {isNestedSeeMoreDesign
                ? !showAllProjects && hiddenProjects.length > 0 && (
                    <button
                        type="button"
                        onClick={() => setShowAllProjects(true)}
                        className="flex min-h-7 w-full items-center pl-9 text-left group-data-[collapsible=icon]:hidden"
                    >
                        <span className={`text-sm font-medium leading-heading tracking-normal text-grey-400 transition-colors ${theme === 'dark' ? 'hover:text-grey-200' : 'hover:text-grey-900'}`}>
                            {t('SEE_MORE')} ({hiddenProjects.length})
                        </span>
                    </button>
                )
                : hiddenProjects.length > 0 && (
                    <div className="flex items-center justify-between w-full py-1 rounded-[8px] text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-2 w-full text-left group-data-[collapsible=icon]:justify-center">
                                        <div className="flex items-center justify-center w-6 h-6" />
                                        <span className="text-sm font-normal leading-normal group-data-[collapsible=icon]:hidden">
                                            {t('SEE_MORE')} ({hiddenProjects.length})
                                        </span>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    side={isMobile ? "top" : "right"}
                                    align={isMobile ? "end" : "start"}
                                    className={`${isMobile ? "w-64 max-w-[calc(100vw-2rem)]" : "w-48"} max-h-80 overflow-y-auto`}
                                    sideOffset={isMobile ? 8 : 4}
                                    alignOffset={isMobile ? -8 : 0}
                                >
                                    {hiddenProjects.map((group) => (
                                        <DropdownMenuItem
                                            key={group.id}
                                            onClick={() => handleProjectClickFromDropdown(group.id)}
                                            className="flex items-center gap-2"
                                        >
                                            <ProjectFolderIcon color={group.color} />
                                            <span className="truncate">{group.name}</span>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                )}
        </div>
    );
};